// src/registered/site-admin/report_component_library/reportComponentLibraryService.js
//
// Persistence for the Report Component Library.
//
// Primary store: the `esg-template-elements-management` S3 bucket, one JSON per
// component under `library/<id>.json`, plus a `library/components.index.json`
// manifest. Reads go through the working `doorman` handshake; writes through the
// GenericFileUploader batch pipeline (same path TemplateStorageService uses).
//
// Fallback store: localStorage. Every save is mirrored locally, and the library
// stays fully usable if the bucket's Lambda permissions are not wired yet — the
// controller surfaces remote failure as a non-blocking warning.

import { GenericFileUploader } from "../../upload/services/genericFileUploader";
import { executeWorkflowHandshake } from "../components/Workflow-esg/Workflow_XLS_Support";
import ReportComponentDTO from "./ReportComponentDTO";
import { SEED_COMPONENTS } from "./seedComponents";

const ELEMENTS_BUCKET =
  process.env.REACT_APP_SITE_TEMPLATE_ELEMENTS_BUCKET ||
  "esg-template-elements-management";

const LIBRARY_PREFIX = "library";
const INDEX_KEY = `${LIBRARY_PREFIX}/components.index.json`;
const LOCAL_KEY = "sustina.reportComponentLibrary.v1";

const userId = () => sessionStorage.getItem("SEC_USER_GUID") || "SYSTEM_USER";
const nowIso = () => new Date().toISOString();

// ---------------------------------------------------------------------------
// local mirror
// ---------------------------------------------------------------------------

function readLocal() {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" && parsed.items ? parsed : { items: {} };
  } catch {
    return { items: {} };
  }
}

function writeLocal(store) {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(store));
  } catch {
    /* private mode / quota — remote is still the source of truth */
  }
}

function upsertLocal(componentJson) {
  const store = readLocal();
  store.items[componentJson.id] = componentJson;
  writeLocal(store);
}

// ---------------------------------------------------------------------------
// remote (S3 via doorman + uploader)
// ---------------------------------------------------------------------------

async function readRemoteJson(key) {
  const res = await executeWorkflowHandshake(
    "doorman/InfraExists",
    {
      action: "READ_JSON_OBJECT",
      user_id: userId(),
      payload: { bucketName: ELEMENTS_BUCKET, key },
    },
    "InfraExists",
  );
  return res?.success ? res.data ?? null : null;
}

async function writeRemoteJson(key, body, labels) {
  const result = await GenericFileUploader.uploadGeneratedData(body, {
    bucketName: ELEMENTS_BUCKET,
    fileName: key,
    labels,
    batchType: 2,
  });
  if (!result?.success) {
    throw new Error(result?.message || `Upload failed for ${key}`);
  }
  return result;
}

function buildIndex(items) {
  return {
    bucket: ELEMENTS_BUCKET,
    prefix: LIBRARY_PREFIX,
    updatedAt: nowIso(),
    items: items
      .slice()
      .sort((a, b) => String(a.label).localeCompare(String(b.label))),
  };
}

// ---------------------------------------------------------------------------
// normalisation
// ---------------------------------------------------------------------------

function normalise(dtoLike, { existing } = {}) {
  const dto = new ReportComponentDTO(dtoLike);
  const clean = (v) => (v == null ? "" : String(v).trim());

  dto.label = clean(dto.label);
  dto.categoryTag = clean(dto.categoryTag);
  dto.metaDescription = clean(dto.metaDescription);
  dto.bindingCapability.allowedDataTypeFamily = clean(
    dto.bindingCapability.allowedDataTypeFamily,
  );

  dto.boundDisclosures = dto.boundDisclosures.map((d) => ({
    id: d.id,
    rowId: d.rowId != null && d.rowId !== "" ? String(d.rowId).trim() : null,
    xbrlTag: clean(d.xbrlTag),
    measureName: clean(d.measureName),
    headerId: d.headerId != null && d.headerId !== "" ? String(d.headerId).trim() : null,
    label: clean(d.label) || clean(d.measureName),
    renderType: clean(d.renderType),
    guidance: clean(d.guidance),
  }));

  // The component's own label is just a rollup of its disclosures' labels.
  if (!dto.label) {
    dto.label = dto.boundDisclosures
      .map((d) => d.label)
      .filter(Boolean)
      .join(" · ");
  }

  dto.slots = dto.slots.map((slot, index) => {
    slot.slotKey = clean(slot.slotKey);
    slot.label = clean(slot.label);
    slot.guidance = clean(slot.guidance);
    slot.unitFamily = clean(slot.unitFamily);
    slot.sortOrder = index;
    slot.options =
      slot.valueType === "enum"
        ? slot.options
            .map((opt, i) => ({
              ...opt,
              optionLabel: clean(opt.optionLabel),
              optionValue: clean(opt.optionValue),
              sortOrder: i,
            }))
            .filter((opt) => opt.optionLabel || opt.optionValue)
        : [];
    return slot;
  });

  dto.createdBy = existing?.createdBy || dto.createdBy || userId();
  dto.createdAt = existing?.createdAt || dto.createdAt || nowIso();
  dto.updatedAt = nowIso();
  dto.version = existing ? (existing.version || 1) + 1 : dto.version || 1;

  return dto;
}

// ---------------------------------------------------------------------------
// public API
// ---------------------------------------------------------------------------

/**
 * List every component. Merges the remote index with the local mirror so
 * nothing is lost when the bucket is unreachable.
 * @returns {Promise<{ items: Array, remoteOk: boolean, remoteError: string }>}
 */
export async function listComponents() {
  const local = readLocal();
  const localSummaries = Object.values(local.items).map((c) =>
    new ReportComponentDTO(c).toSummary(),
  );

  let remoteItems = [];
  let remoteOk = false;
  let remoteError = "";

  try {
    const index = await readRemoteJson(INDEX_KEY);
    if (index && Array.isArray(index.items)) {
      remoteItems = index.items;
      remoteOk = true;
    } else {
      remoteOk = true; // reachable, just empty
    }
  } catch (err) {
    remoteError = err?.message || "Component library bucket unreachable.";
  }

  const byId = new Map();
  // seeds first (lowest priority) — a saved copy with the same id wins
  SEED_COMPONENTS.forEach((c) => byId.set(c.id, c.toSummary()));
  localSummaries.forEach((s) => byId.set(s.id, s));
  remoteItems.forEach((s) => byId.set(s.id, { ...byId.get(s.id), ...s }));

  return {
    items: [...byId.values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    ),
    remoteOk,
    remoteError,
  };
}

/**
 * Load one full component definition (remote first, then local mirror).
 * @param {string} id
 * @returns {Promise<ReportComponentDTO|null>}
 */
export async function getComponent(id) {
  if (!id) return null;
  try {
    const raw = await readRemoteJson(`${LIBRARY_PREFIX}/${id}.json`);
    if (raw) return new ReportComponentDTO(raw);
  } catch {
    /* fall through to local */
  }
  const local = readLocal();
  if (local.items[id]) return new ReportComponentDTO(local.items[id]);
  const seed = SEED_COMPONENTS.find((c) => c.id === id);
  return seed ? new ReportComponentDTO(JSON.parse(JSON.stringify(seed))) : null;
}

/**
 * Create or update a component. Always mirrors locally; attempts remote.
 * @param {Object} dtoLike
 * @param {{ existing?: Object }} [ctx]
 * @returns {Promise<{ component: ReportComponentDTO, persistedRemote: boolean, remoteError: string }>}
 */
export async function saveComponent(dtoLike, ctx = {}) {
  const component = normalise(dtoLike, ctx);
  const json = JSON.parse(JSON.stringify(component));

  upsertLocal(json);

  let persistedRemote = false;
  let remoteError = "";

  try {
    await writeRemoteJson(
      `${LIBRARY_PREFIX}/${component.id}.json`,
      json,
      ["ReportComponentLibrary", "Component", component.renderType],
    );

    let indexItems = [];
    try {
      const index = await readRemoteJson(INDEX_KEY);
      indexItems = index && Array.isArray(index.items) ? index.items : [];
    } catch {
      indexItems = [];
    }
    const pos = indexItems.findIndex((i) => i.id === component.id);
    if (pos >= 0) indexItems[pos] = component.toSummary();
    else indexItems.push(component.toSummary());

    await writeRemoteJson(INDEX_KEY, buildIndex(indexItems), [
      "ReportComponentLibrary",
      "Index",
    ]);
    persistedRemote = true;
  } catch (err) {
    remoteError = err?.message || "Could not sync component to the cloud store.";
  }

  return { component, persistedRemote, remoteError };
}

/**
 * Soft-delete: flip isActive to false and persist.
 * @param {string} id
 */
export async function deactivateComponent(id) {
  const existing = await getComponent(id);
  if (!existing) throw new Error(`Component not found: ${id}`);
  return saveComponent(
    { ...existing, isActive: false },
    { existing: JSON.parse(JSON.stringify(existing)) },
  );
}
